import React, { useCallback, useEffect, useState } from 'react';
import { greenCategories, roastRecords, coffeeDispatches } from '../lib/api';
import { Plus, Flame, Trash2, Download } from 'lucide-react';
import { format, startOfDay, endOfDay, subDays, startOfMonth, endOfMonth, subMonths } from 'date-fns';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { inr, rs } from '../lib/format';
import { downloadPdfReport } from '../lib/pdfReport';

const PRESETS = [
  { k: 'all', label: 'All Time' },
  { k: 'today', label: 'Today' },
  { k: 'yesterday', label: 'Yesterday' },
  { k: 'last_7', label: 'Last 7 Days' },
  { k: 'this_month', label: 'This Month' },
  { k: 'last_month', label: 'Last Month' },
  { k: 'custom', label: 'Custom Range' },
];

function computeRange(preset, customFrom, customTo) {
  const now = new Date();
  switch (preset) {
    case 'today': return { from: startOfDay(now), to: endOfDay(now), label: 'Today' };
    case 'yesterday': { const y = subDays(now, 1); return { from: startOfDay(y), to: endOfDay(y), label: 'Yesterday' }; }
    case 'last_7': return { from: startOfDay(subDays(now, 6)), to: endOfDay(now), label: 'Last 7 Days' };
    case 'this_month': return { from: startOfMonth(now), to: endOfMonth(now), label: 'This Month' };
    case 'last_month': { const lm = subMonths(now, 1); return { from: startOfMonth(lm), to: endOfMonth(lm), label: 'Last Month' }; }
    case 'custom':
      if (!customFrom || !customTo) return { from: null, to: null, label: 'Custom' };
      return { from: startOfDay(new Date(customFrom)), to: endOfDay(new Date(customTo)), label: `${customFrom} to ${customTo}` };
    default: return { from: null, to: null, label: 'All Time' };
  }
}

const Roasting = () => {
  const [cats, setCats] = useState([]);
  const [records, setRecords] = useState([]);
  const [costs, setCosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    category_id: '',
    roast_level: 'Medium',
    green_input_kg: 0,
    roasted_output_kg: 0,
    roast_date: new Date().toISOString().split('T')[0],
    notes: '',
  });
  const [preset, setPreset] = useState('all');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');

  const fetchAll = useCallback(async () => {
    try {
      const [c, r, k] = await Promise.all([
        greenCategories.getAll(), roastRecords.getAll(), coffeeDispatches.getCosts(),
      ]);
      setCats(c.data);
      setRecords(r.data);
      setCosts(k.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const g = parseFloat(form.green_input_kg);
    const o = parseFloat(form.roasted_output_kg);
    if (o > g) return alert('Roasted output cannot exceed green input');
    try {
      await roastRecords.create({
        ...form,
        green_input_kg: g,
        roasted_output_kg: o,
        roast_date: new Date(form.roast_date).toISOString(),
      });
      setShowForm(false);
      setForm({
        category_id: '',
        roast_level: 'Medium',
        green_input_kg: 0,
        roasted_output_kg: 0,
        roast_date: new Date().toISOString().split('T')[0],
        notes: '',
      });
      fetchAll();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to save roast');
    }
  };

  const handleDelete = async (rec) => {
    if (!window.confirm(`Delete this roast? ${rec.green_input_kg}kg will be returned to green stock and ${rec.roasted_output_kg}kg removed from roasted stock.`)) return;
    try {
      await roastRecords.delete(rec.id);
      fetchAll();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const range = computeRange(preset, customFrom, customTo);
  const filtered = records.filter((r) => {
    if (!range.from || !range.to) return true;
    const d = new Date(r.roast_date);
    return d >= range.from && d <= range.to;
  });

  const totalGreen = filtered.reduce((s, r) => s + r.green_input_kg, 0);
  const totalRoasted = filtered.reduce((s, r) => s + r.roasted_output_kg, 0);
  const totalWastage = filtered.reduce((s, r) => s + r.wastage_kg, 0);
  const avgYield = totalGreen > 0 ? ((totalRoasted / totalGreen) * 100).toFixed(1) : '0.0';
  const greenCostFor = (catId) => costs.find((c) => c.category_id === catId)?.green_cost_per_kg || 0;
  const totalWastageCost = filtered.reduce((s, r) => s + r.wastage_kg * greenCostFor(r.category_id), 0);

  // Avg yield per category × roast level
  const yieldAgg = {};
  filtered.forEach((r) => {
    const key = `${r.category_name} · ${r.roast_level}`;
    const a = yieldAgg[key] || { name: key, level: r.roast_level, inKg: 0, outKg: 0 };
    a.inKg += r.green_input_kg;
    a.outKg += r.roasted_output_kg;
    yieldAgg[key] = a;
  });
  const yieldChart = Object.values(yieldAgg)
    .filter((a) => a.inKg > 0)
    .map((a) => ({ name: a.name, level: a.level, yieldPct: +((a.outKg / a.inKg) * 100).toFixed(1) }))
    .sort((a, b) => b.yieldPct - a.yieldPct);
  const LEVEL_COLORS = { Light: '#fbbf24', Medium: '#f59e0b', Dark: '#44403c' };

  const handleDownloadPdf = () => {
    downloadPdfReport({
      title: 'Roasting Report',
      subtitle: range.label,
      summary: [
        { label: 'Roasts', value: filtered.length },
        { label: 'Green Input', value: `${totalGreen.toFixed(2)} kg` },
        { label: 'Roasted Output', value: `${totalRoasted.toFixed(2)} kg` },
        { label: 'Wastage', value: `${totalWastage.toFixed(2)} kg` },
        { label: 'Wastage Cost', value: rs(totalWastageCost) },
        { label: 'Yield %', value: `${avgYield}%` },
      ],
      tables: [
        {
          title: 'Roast Records',
          head: ['Date', 'Batch', 'Category', 'Roast Level', 'Green (kg)', 'Roasted (kg)', 'Wastage (kg)', 'Yield %', 'Roasted By', 'Notes'],
          rows: filtered.map((r) => [
            format(new Date(r.roast_date), 'yyyy-MM-dd'),
            r.batch_no || '-',
            r.category_name,
            r.roast_level,
            r.green_input_kg.toFixed(2),
            r.roasted_output_kg.toFixed(2),
            r.wastage_kg.toFixed(2),
            r.green_input_kg > 0 ? `${((r.roasted_output_kg / r.green_input_kg) * 100).toFixed(1)}%` : '-',
            r.roasted_by || '-',
            r.notes || '-',
          ]),
        },
      ],
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-amber-500 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="roasting-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">Roasting</h1>
          <p className="text-gray-600 mt-1">Log roast batches — wastage is calculated automatically</p>
        </div>
        <div className="flex gap-3">
          <button onClick={handleDownloadPdf} data-testid="download-roasting-pdf" className="flex items-center space-x-2 bg-gradient-to-r from-amber-500 to-orange-600 text-white px-5 py-2.5 rounded-lg font-semibold hover:from-amber-600 hover:to-orange-700 transition shadow">
            <Download className="w-4 h-4" /><span>Download PDF</span>
          </button>
          <button onClick={() => setShowForm(true)} disabled={cats.length === 0} data-testid="new-roast-button"
            className="flex items-center space-x-2 bg-gradient-to-r from-orange-500 to-red-600 text-white px-5 py-2.5 rounded-lg font-semibold hover:from-orange-600 hover:to-red-700 transition shadow disabled:opacity-50">
            <Plus className="w-4 h-4" /><span>New Roast</span>
          </button>
        </div>
      </div>

      {/* Date preset */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 space-y-3">
        <div className="flex flex-wrap gap-2">
          {PRESETS.map((p) => (
            <button key={p.k} onClick={() => setPreset(p.k)} data-testid={`roast-preset-${p.k}`}
              className={`px-3 py-1.5 text-sm rounded-lg font-medium transition ${preset === p.k ? 'bg-amber-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
              {p.label}
            </button>
          ))}
        </div>
        {preset === 'custom' && (
          <div className="flex gap-3 pt-2 border-t">
            <input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} className="px-3 py-2 border rounded-lg" />
            <input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} className="px-3 py-2 border rounded-lg" />
          </div>
        )}
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <p className="text-xs text-gray-500 uppercase">Roasts</p>
          <p className="text-2xl font-bold text-gray-800 mt-1">{filtered.length}</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <p className="text-xs text-gray-500 uppercase">Green Used</p>
          <p className="text-2xl font-bold text-green-700 mt-1">{totalGreen.toFixed(1)} kg</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <p className="text-xs text-gray-500 uppercase">Roasted</p>
          <p className="text-2xl font-bold text-orange-600 mt-1">{totalRoasted.toFixed(1)} kg</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <p className="text-xs text-gray-500 uppercase">Wastage</p>
          <p className="text-2xl font-bold text-red-600 mt-1">{totalWastage.toFixed(1)} kg</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <p className="text-xs text-gray-500 uppercase">Wastage Cost</p>
          <p className="text-2xl font-bold text-rose-600 mt-1" data-testid="wastage-cost-stat">{inr(totalWastageCost)}</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <p className="text-xs text-gray-500 uppercase">Avg Yield</p>
          <p className="text-2xl font-bold text-amber-600 mt-1">{avgYield}%</p>
        </div>
      </div>

      {/* Yield analytics */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5" data-testid="yield-analytics-card">
        <h3 className="text-lg font-bold text-gray-800 mb-1">Roast Yield by Coffee & Level</h3>
        <p className="text-xs text-gray-500 mb-4">Average yield % (roasted output ÷ green input) for the selected period — spot consistency drift</p>
        {yieldChart.length === 0 ? (
          <p className="text-center text-gray-400 py-8 text-sm">No roasts in this period.</p>
        ) : (
          <ResponsiveContainer width="100%" height={Math.max(180, yieldChart.length * 42)}>
            <BarChart data={yieldChart} layout="vertical" margin={{ top: 5, right: 40, left: 10, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
              <XAxis type="number" domain={[0, 100]} tickFormatter={(v) => `${v}%`} tick={{ fontSize: 11 }} />
              <YAxis type="category" dataKey="name" width={180} tick={{ fontSize: 11 }} />
              <Tooltip formatter={(v) => [`${v}%`, 'Yield']} />
              <Bar dataKey="yieldPct" radius={[0, 5, 5, 0]} barSize={18} label={{ position: 'right', formatter: (v) => `${v}%`, fontSize: 11 }}>
                {yieldChart.map((e, idx) => (
                  <Cell key={e.name} fill={LEVEL_COLORS[e.level] || '#f59e0b'} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {filtered.length === 0 ? (
          <div className="py-12 text-center text-gray-500">
            <Flame className="w-12 h-12 mx-auto mb-3 opacity-40" />
            <p>No roasts in this period.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left py-3 px-6 text-sm font-semibold text-gray-700">Date</th>
                  <th className="text-left py-3 px-6 text-sm font-semibold text-gray-700">Batch</th>
                  <th className="text-left py-3 px-6 text-sm font-semibold text-gray-700">Category</th>
                  <th className="text-left py-3 px-6 text-sm font-semibold text-gray-700">Roast Level</th>
                  <th className="text-right py-3 px-6 text-sm font-semibold text-gray-700">Green (kg)</th>
                  <th className="text-right py-3 px-6 text-sm font-semibold text-gray-700">Roasted (kg)</th>
                  <th className="text-right py-3 px-6 text-sm font-semibold text-gray-700">Wastage (kg)</th>
                  <th className="text-right py-3 px-6 text-sm font-semibold text-gray-700">Yield</th>
                  <th className="text-left py-3 px-6 text-sm font-semibold text-gray-700">Roasted By</th>
                  <th className="text-center py-3 px-6 text-sm font-semibold text-gray-700"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => (
                  <tr key={r.id} className="border-t hover:bg-gray-50">
                    <td className="py-3 px-6 text-sm">{format(new Date(r.roast_date), 'MMM dd, yyyy')}</td>
                    <td className="py-3 px-6 text-sm font-mono text-gray-600">{r.batch_no || '-'}</td>
                    <td className="py-3 px-6 font-medium text-gray-800">{r.category_name}</td>
                    <td className="py-3 px-6">
                      <span className={`inline-block px-2 py-0.5 text-xs rounded-full font-semibold ${
                        r.roast_level === 'Light' ? 'bg-yellow-100 text-yellow-800' :
                        r.roast_level === 'Dark' ? 'bg-stone-700 text-white' :
                        'bg-amber-100 text-amber-800'
                      }`}>{r.roast_level}</span>
                    </td>
                    <td className="py-3 px-6 text-right">{r.green_input_kg.toFixed(2)}</td>
                    <td className="py-3 px-6 text-right font-semibold text-orange-600">{r.roasted_output_kg.toFixed(2)}</td>
                    <td className="py-3 px-6 text-right text-red-600">{r.wastage_kg.toFixed(2)}</td>
                    <td className="py-3 px-6 text-right text-sm">{r.green_input_kg > 0 ? `${((r.roasted_output_kg / r.green_input_kg) * 100).toFixed(1)}%` : '-'}</td>
                    <td className="py-3 px-6 text-sm text-gray-600">{r.roasted_by || '-'}</td>
                    <td className="py-3 px-6 text-center">
                      <button onClick={() => handleDelete(r)} data-testid={`delete-roast-${r.id}`} className="p-1.5 text-red-600 hover:bg-red-50 rounded" title="Delete">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full">
            <div className="px-6 py-4 border-b flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-800">New Roast Entry</h2>
              <button onClick={() => setShowForm(false)} className="text-gray-400 hover:text-gray-600 text-2xl">×</button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Green Coffee Category *</label>
                <select required value={form.category_id}
                  onChange={(e) => setForm({ ...form, category_id: e.target.value })}
                  data-testid="roast-category-select"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500">
                  <option value="">— select category —</option>
                  {cats.map((c) => (
                    <option key={c.id} value={c.id}>{c.name} (Available: {(c.current_stock_kg || 0).toFixed(2)}kg)</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Roast Level *</label>
                <div className="grid grid-cols-3 gap-2">
                  {['Light', 'Medium', 'Dark'].map((lvl) => (
                    <button
                      key={lvl} type="button"
                      data-testid={`roast-level-${lvl.toLowerCase()}`}
                      onClick={() => setForm({ ...form, roast_level: lvl })}
                      className={`py-2 rounded-lg font-semibold text-sm border-2 transition ${
                        form.roast_level === lvl
                          ? (lvl === 'Light' ? 'bg-yellow-400 text-white border-yellow-500' :
                             lvl === 'Dark' ? 'bg-stone-700 text-white border-stone-800' :
                             'bg-amber-500 text-white border-amber-600')
                          : 'bg-white text-gray-700 border-gray-200 hover:border-amber-400'
                      }`}>
                      {lvl} Roast
                    </button>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Green Input (kg) *</label>
                  <input type="number" step="0.01" required value={form.green_input_kg}
                    onChange={(e) => setForm({ ...form, green_input_kg: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Roasted Output (kg) *</label>
                  <input type="number" step="0.01" required value={form.roasted_output_kg}
                    onChange={(e) => setForm({ ...form, roasted_output_kg: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500" />
                </div>
              </div>
              {form.green_input_kg > 0 && form.roasted_output_kg > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-900">
                  <strong>Auto-calculated wastage:</strong> {(parseFloat(form.green_input_kg) - parseFloat(form.roasted_output_kg)).toFixed(2)} kg
                  ({parseFloat(form.green_input_kg) > 0 ? (((parseFloat(form.green_input_kg) - parseFloat(form.roasted_output_kg)) / parseFloat(form.green_input_kg)) * 100).toFixed(1) : '0'}% shrinkage)
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Roast Date *</label>
                <input type="date" required value={form.roast_date}
                  onChange={(e) => setForm({ ...form, roast_date: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea rows="2" value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg" />
              </div>
              <div className="flex justify-end gap-3 pt-3 border-t">
                <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 border rounded-lg hover:bg-gray-50">Cancel</button>
                <button type="submit" data-testid="roast-save-button" className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 font-semibold">Save Roast</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Roasting;
